/* OTERVANO mobile menu and native Shopify Inbox shortcuts. No commerce or tracking writes. */
(() => {
  'use strict';
  if (customElements.get('ov-mobile-tools')) return;
  const visible = el => !!el && !el.hidden && !!el.getClientRects().length;
  const bridge = { open(message, trigger) { return window.OtervanoInbox?.open(message, trigger); } };
  class OvMobileTools extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abort = new AbortController();
      this.originals = new Map();
      this.dialog = this.querySelector('dialog');
      this.config = {};
      try { this.config = JSON.parse(this.querySelector('[data-ovm-config]').textContent); } catch (_) {}
      this.media = matchMedia('(max-width: 749px)');
      const opts = { signal: this.abort.signal };
      document.addEventListener('click', event => this.routeClick(event), { ...opts, capture: true });
      this.dialog?.addEventListener('close', () => this.cleanupDialog(), opts);
      this.dialog?.addEventListener('cancel', () => this.closeMenu(), opts);
      this.media.addEventListener('change', () => { if (!this.media.matches) this.closeMenu(); }, opts);
      window.addEventListener('resize', () => this.positionMenu(), opts);
      window.visualViewport?.addEventListener('resize', () => this.positionMenu(), opts);
      document.addEventListener('shopify:section:load', () => this.enhanceSupport(), opts);
      this.enhanceSupport();
      const main = document.getElementById('MainContent');
      if (main) {
        this.observer = new MutationObserver(records => {
          for (const [slot] of this.originals) if (!slot.isConnected) this.originals.delete(slot);
          if (!records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 &&
            (node.matches('.ovs-trust,[data-service-item]') || node.querySelector('.ovs-trust,[data-service-item]'))))) return;
          cancelAnimationFrame(this.raf);
          this.raf = requestAnimationFrame(() => this.enhanceSupport());
        });
        this.observer.observe(main, { childList: true, subtree: true });
      }
    }
    disconnectedCallback() {
      this.closeMenu(); this.abort?.abort(); this.observer?.disconnect(); cancelAnimationFrame(this.raf);
      for (const [slot, original] of this.originals || []) {
        const item = slot.querySelector('.ovs-item'); if (item && slot.isConnected) { item.replaceWith(original); delete slot.dataset.ovmAction; }
      }
      this.originals?.clear(); this.initialized = false;
    }
    routeClick(event) {
      const target = event.target instanceof Element ? event.target : event.target?.parentElement;
      if (!target) return;
      const opener = target.closest('#Details-menu-drawer-container > summary');
      if (opener && this.media.matches && this.config.menuEnabled && this.dialog?.showModal) {
        event.preventDefault(); event.stopImmediatePropagation();
        if (this.dialog.open) this.closeMenu(); else this.openMenu(opener);
        return;
      }
      const close = target.closest('[data-ovm-close]');
      if (close && this.contains(close)) { event.preventDefault(); this.closeMenu(); return; }
      const chat = target.closest('[data-ov-inbox-open]');
      if (chat && (this.contains(chat) || chat.closest('.ovs-trust[data-ov-trust="8"]'))) {
        event.preventDefault(); event.stopImmediatePropagation();
        this.closeMenu();
        requestAnimationFrame(() => bridge.open(this.config.unavailable, chat));
      }
      if (event.target === this.dialog) this.closeMenu();
    }
    openMenu(opener) {
      this.opener = opener;
      this.oldExpanded = opener.getAttribute('aria-expanded');
      this.oldControls = opener.getAttribute('aria-controls');
      this.oldHeader = document.documentElement.style.getPropertyValue('--ovm-header');
      document.documentElement.style.setProperty('--ovm-header', this.config.headerBg || '#222f28');
      opener.setAttribute('aria-controls', this.dialog.id); opener.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('ovm-open'); this.positionMenu(true);
      this.dialog.showModal(); this.dialog.querySelector('[data-ovm-close]')?.focus();
    }
    positionMenu(force = false) {
      if (!this.dialog?.open && !force) return;
      const header = document.querySelector('.section-header');
      const viewport = window.visualViewport?.height || window.innerHeight;
      const top = Math.max(0, Math.min(header?.getBoundingClientRect().bottom || 0, viewport * .36));
      this.dialog.style.setProperty('--ovm-top', `${Math.round(top)}px`);
    }
    closeMenu() { if (this.dialog?.open) this.dialog.close(); this.cleanupDialog(); }
    cleanupDialog() {
      if (!this.opener) return;
      document.documentElement.classList.remove('ovm-open');
      const restore = (name, value) => value === null ? this.opener.removeAttribute(name) : this.opener.setAttribute(name, value);
      restore('aria-expanded', this.oldExpanded); restore('aria-controls', this.oldControls);
      if (this.oldHeader) document.documentElement.style.setProperty('--ovm-header', this.oldHeader);
      else document.documentElement.style.removeProperty('--ovm-header');
      this.opener = null;
    }
    enhanceSupport() {
      if (!this.config.supportEnabled) return;
      document.querySelectorAll('.ovs-trust[data-ov-trust="8"] [data-service-item]').forEach(slot => {
        const action = this.config.actions?.[String(slot.dataset.serviceItem)];
        if (!action || action.mode === 'existing') return;
        let original = this.originals.get(slot);
        if (!original) {
          const item = slot.querySelector('.ovs-item'); if (!item) return;
          original = item.cloneNode(true); this.originals.set(slot, original);
        }
        const signature = `${action.mode}|${action.url || ''}`;
        if (slot.dataset.ovmAction === signature) return;
        let url = '';
        if (action.mode === 'link' && action.url) {
          try { const candidate = new URL(action.url, document.baseURI);
            if (['http:', 'https:', 'mailto:', 'tel:'].includes(candidate.protocol)) url = action.url;
          } catch (_) {}
        }
        const tag = action.mode === 'chat' ? 'button' : url ? 'a' : 'div';
        const replacement = document.createElement(tag);
        for (const attr of original.attributes) {
          if (!['href', 'target', 'rel', 'type', 'role'].includes(attr.name)) replacement.setAttribute(attr.name, attr.value);
        }
        replacement.classList.remove('ovs-item--link');
        replacement.append(...[...original.childNodes].map(node => node.cloneNode(true)));
        replacement.querySelectorAll('.visually-hidden').forEach(node => node.remove());
        if (tag === 'button') {
          replacement.type = 'button'; replacement.dataset.ovInboxOpen = '';
          replacement.classList.add('ovm-support-action'); replacement.setAttribute('aria-haspopup', 'dialog');
        } else if (tag === 'a') {
          replacement.href = url; replacement.classList.add('ovs-item--link');
        }
        slot.querySelector('.ovs-item')?.replaceWith(replacement);
        slot.dataset.ovmAction = signature;
      });
    }
  }
  customElements.define('ov-mobile-tools', OvMobileTools);
})();
